import React, { useState } from "react";
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Fade,
  Backdrop,
} from "@mui/material";
import { Edit, Trash2, Eye, AlertTriangle, X, Layers, Info } from "lucide-react";
import { alpha } from "@mui/material";
import formatDate from "../../../../Utils/formatDate";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsEditMode,
  setShowUnitInfos,
} from "../../../../redux/itemUnit/productItemUnitSlice";
import { setRefresh } from "../../../../redux/refreshSlice";
import { useDestroy } from "../../../../hooks/useDestroy";
import StockBatchDialog from "./StockBatchDialog";

const ProductItemUnitListRow = ({ row, index }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showStockBatch, setShowStockBatch] = useState(false);
  const { destroyData, loading } = useDestroy("item-unit");
  const dispatch = useDispatch();
  const { refresh } = useSelector((state) => state.refresh);
  const { editItemUnit } = useSelector((state) => state.productItemUnit);

  const handleEdit = () => {
    dispatch(
      setIsEditMode({ data: row, isEditMode: !editItemUnit.isEditMode }),
    );
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await destroyData(row._id);
    if (result) {
      dispatch(setRefresh(!refresh));
      setDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const getStockStatus = (currentQty, reorderQty) => {
    if (currentQty === 0) {
      return { label: "Out of Stock", color: "error" };
    } else if (currentQty <= reorderQty) {
      return { label: "Low Stock", color: "warning" };
    } else {
      return { label: "In Stock", color: "success" };
    }
  };

  const stockStatus = getStockStatus(row.qty, row.reOrderQty);

  const handleUnitInfos = () => {
    // setShowStockBatch(true);
    dispatch(setShowUnitInfos({ data: row, isShowUnitInfos: true }));
  };

  return (
    <>
      <TableRow
        key={row._id}
        sx={{
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell>
          <Typography variant="body2" fontWeight={10}>
            {index + 1}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={200}>
            {row.name}
          </Typography>
        </TableCell>
        {/* <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.sellingPrice.toLocaleString()} MMK
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.purchasePrice.toLocaleString()} MMK
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color:
                  row.qty === 0
                    ? "error.main"
                    : row.qty <= row.reOrderQty
                      ? "warning.main"
                      : "success.main",
              }}
            >
              {row.qty}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.reOrderQty}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={stockStatus.label}
            color={stockStatus.color}
            size="small"
            variant="outlined"
          />
        </TableCell> */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(row.createdAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title="Unit Info">
              <IconButton size="small" onClick={handleUnitInfos}>
                <Info size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  color: "warning.main",
                  "&:hover": {
                    backgroundColor: "warning.main",
                    color: "white",
                  },
                }}
              >
                <Edit size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={loading ? "Deleting..." : "Delete"}>
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                disabled={loading}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "white",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Trash2 size={16} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <X size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 4 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash2 size={24} color="white" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: "white", fontWeight: 600, mb: 0.5 }}
              >
                Delete Product Item Unit
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Warning alert with enhanced styling */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              p: 2.5,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 2,
              mb: 3,
            }}
          >
            <AlertTriangle size={20} color="#ef4444" style={{ marginTop: 2 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: "#991b1b", fontWeight: 600, mb: 0.5 }}
              >
                Warning: Permanent Deletion
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#7f1d1d", lineHeight: 1.5 }}
              >
                This action cannot be undone. The product item unit will be
                permanently deleted from the system.
              </Typography>
            </Box>
          </Box>

          {/* Confirmation message */}
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: "1.1rem" }}
          >
            Are you sure you want to delete this product item unit?
          </Typography>

          {/* Item details card */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#64748b", fontWeight: 600, mb: 1.5 }}
            >
              UNIT TO DELETE
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "#1e293b", fontWeight: 600, mb: 1 }}
            >
              {row?.name || "Unknown Unit"}
            </Typography>
            <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Selling Price
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.sellingPrice?.toLocaleString() || 0} MMK
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Purchase Price
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.purchasePrice?.toLocaleString() || 0} MMK
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Quantity
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.qty || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Enhanced action buttons */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            startIcon={loading ? null : <Trash2 size={18} />}
            disabled={loading}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
              "&:disabled": {
                backgroundColor: "#fca5a5",
                color: "white",
              },
            }}
          >
            {loading ? "Deleting..." : "Delete Unit"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* <StockBatchDialog
        selectedProductItemUnit={row}
        open={showStockBatch}
        onClose={() => setShowStockBatch(false)}
      /> */}
    </>
  );
};

export default ProductItemUnitListRow;
